import { useCallback, useEffect, useRef, useState } from 'react'
import { priceConcernAdminService } from '../services'
import { getApiErrorMessage } from '../services/apiClient'
import { useDebouncedValue } from './useDebouncedValue'
import {
  buildPriceConcernQuery,
  downloadBlobResponse,
} from '../components/price-concerns/priceConcernUtils'
import usePriceConcernBadgeStore from '../store/priceConcernBadgeStore'

const SEARCH_DEBOUNCE_MS = 450

export function usePriceConcerns(filters) {
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState({
    notReviewedCount: 0,
    reviewedCount: 0,
    averageReportedGap: null,
  })
  const [listLoading, setListLoading] = useState(true)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState(null)
  const [statusUpdatingId, setStatusUpdatingId] = useState(null)

  const debouncedSearch = useDebouncedValue(filters.search || '', SEARCH_DEBOUNCE_MS)
  const listReqId = useRef(0)
  const summaryReqId = useRef(0)
  const refreshBadge = usePriceConcernBadgeStore((s) => s.refresh)

  const page = filters.page ?? 0
  const rowsPerPage = filters.rowsPerPage ?? 20
  const status = filters.status || ''
  const datePreset = filters.datePreset || ''
  const dateFrom = filters.dateFrom || ''
  const dateTo = filters.dateTo || ''

  const filterState = {
    search: debouncedSearch,
    status,
    datePreset,
    dateFrom,
    dateTo,
    page,
    rowsPerPage,
  }

  const loadList = useCallback(async () => {
    const reqId = ++listReqId.current
    setListLoading(true)
    try {
      const params = buildPriceConcernQuery(filterState, {
        includePagination: true,
      })
      const data = await priceConcernAdminService.list(params)
      if (reqId !== listReqId.current) return null
      const items = Array.isArray(data?.items) ? data.items : []
      const nextTotal = Number(data?.pagination?.total) || 0
      setRows(items)
      setTotal(nextTotal)
      setError(null)
      return { items, total: nextTotal }
    } catch (err) {
      if (reqId !== listReqId.current) return null
      setError(getApiErrorMessage(err, 'Failed to load price concerns'))
      setRows([])
      setTotal(0)
      return { items: [], total: 0 }
    } finally {
      if (reqId === listReqId.current) setListLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional field deps
  }, [debouncedSearch, status, datePreset, dateFrom, dateTo, page, rowsPerPage])

  const loadSummary = useCallback(async () => {
    const reqId = ++summaryReqId.current
    setSummaryLoading(true)
    try {
      // Overall workload totals — independent of table filters (SCRUM-655).
      const params = buildPriceConcernQuery({}, { includePagination: false })
      const data = await priceConcernAdminService.summary(params)
      if (reqId !== summaryReqId.current) return
      setSummary({
        notReviewedCount: Number(data?.notReviewedCount) || 0,
        reviewedCount: Number(data?.reviewedCount) || 0,
        averageReportedGap:
          data?.averageReportedGap == null
            ? null
            : Number(data.averageReportedGap),
      })
    } catch {
      if (reqId !== summaryReqId.current) return
    } finally {
      if (reqId === summaryReqId.current) setSummaryLoading(false)
    }
  }, [])

  useEffect(() => {
    loadList()
  }, [loadList])

  useEffect(() => {
    loadSummary()
  }, [loadSummary])

  const updateStatus = useCallback(
    async (id, nextStatus) => {
      let previous = null
      setRows((prev) => {
        previous = prev.find((r) => r.id === id) || null
        if (!previous || previous.status === nextStatus) return prev
        // Drop from the current page when an active status filter no longer matches.
        if (status && nextStatus !== status) {
          return prev.filter((r) => r.id !== id)
        }
        return prev.map((r) =>
          r.id === id ? { ...r, status: nextStatus } : r,
        )
      })
      if (!previous || previous.status === nextStatus) {
        return { concern: previous, total: null }
      }

      if (status && nextStatus !== status) {
        setTotal((t) => Math.max(0, t - 1))
      }

      setStatusUpdatingId(id)
      try {
        const updated = await priceConcernAdminService.updateStatus(
          id,
          nextStatus,
        )
        // Reload list + pagination so filters stay consistent (SCRUM-655 QA).
        const [listResult] = await Promise.all([
          loadList(),
          loadSummary(),
          refreshBadge(),
        ])
        return {
          concern: updated,
          total: listResult?.total ?? null,
        }
      } catch (err) {
        setRows((prev) => {
          if (status && nextStatus !== status) {
            const exists = prev.some((r) => r.id === id)
            return exists ? prev : [...prev, previous]
          }
          return prev.map((r) => (r.id === id ? previous : r))
        })
        if (status && nextStatus !== status) {
          setTotal((t) => t + 1)
        }
        throw err
      } finally {
        setStatusUpdatingId(null)
      }
    },
    [loadList, loadSummary, refreshBadge, status],
  )

  const exportExcel = useCallback(async () => {
    setExporting(true)
    try {
      const params = buildPriceConcernQuery(
        {
          search: debouncedSearch,
          status,
          datePreset,
          dateFrom,
          dateTo,
        },
        { includePagination: false },
      )
      const response = await priceConcernAdminService.exportExcel(params)
      downloadBlobResponse(response)
    } finally {
      setExporting(false)
    }
  }, [debouncedSearch, status, datePreset, dateFrom, dateTo])

  return {
    rows,
    total,
    summary,
    listLoading,
    summaryLoading,
    exporting,
    error,
    setError,
    statusUpdatingId,
    reload: () => {
      loadList()
      loadSummary()
    },
    markReviewed: (id) => updateStatus(id, 'REVIEWED'),
    updateStatus,
    exportExcel,
  }
}
